import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Card } from '../components/Card'
import { KpiCard } from '../components/KpiCard'
import { Icon, type IconName } from '../components/Icon'
import { TrendChart, type ChartType } from '../components/charts/TrendChart'
import { ChartPeriodPicker, computePeriod } from '../components/ChartPeriodPicker'
import { useCurrency } from '../lib/currency-context'
import type { PeriodKey } from '../lib/period-context'
import { fmtMoney, FMT_INT, SYMBOL, FX } from '../lib/money'
import { useTranslation } from 'react-i18next'

interface HomePayload {
  period: { from: string; to: string; days: number; grain: 'day' | 'month' }
  kpis: {
    revenue:        { minor: number; deltaPct: number }
    profit:         { minor: number; deltaPct: number }
    margin_pct:     number
    shipments:      number
    disputes_open:  { count: number; at_stake_minor: number }
    rebills_open:   { count: number; to_invoice_minor: number }
  }
  trend: Array<{ bucket: string; ships: number; revenue_minor: number; cost_minor: number; profit_minor: number }>
  carriers: Array<{
    code: string; name: string; color: string; icon: string; notes: string
    shipments: number; revenue_minor: number; cost_minor: number; profit_minor: number; margin_pct: number; status: string
  }>
  alerts: Array<{ tone: string; icon: string; title: string; detail: string; cta: string }>
  topCustomers: Array<{ name: string; revenue_minor: number; ships: number }>
}

function useHome(periodKey: PeriodKey) {
  const period = computePeriod(periodKey)
  return useQuery<HomePayload>({
    queryKey: ['home', period.from, period.to],
    queryFn: () => fetch(`/api/home?from=${period.from}&to=${period.to}`).then(r => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json()
    }),
    staleTime: 30_000,
  })
}

export function HomeView() {
  const { t } = useTranslation()
  const { currency } = useCurrency()
  const navigate = useNavigate()

  // Each chart/section has its own period
  const [kpiPeriod,     setKpiPeriod]     = useState<PeriodKey>('ytd')
  const [trendPeriod,   setTrendPeriod]   = useState<PeriodKey>('ytd')
  const [marginPeriod,  setMarginPeriod]  = useState<PeriodKey>('ytd')
  const [customerPeriod,setCustomerPeriod]= useState<PeriodKey>('30d')

  const kpi      = useHome(kpiPeriod)
  const margin   = useHome(marginPeriod)
  const customer = useHome(customerPeriod)

  const sym = SYMBOL[currency]
  const fxScale = FX[currency]

  return (
    <div>
      {/* PAGE HEADER */}
      <div className="mb-10">
        <div className="flex items-center gap-2 text-[12px] font-semibold text-mint-600 uppercase tracking-wider">
          <span className="w-1.5 h-1.5 rounded-full bg-mint-500" /> {t('home.live')}
        </div>
        <h1 className="text-[32px] font-bold tracking-tight mt-2">{t('home.title')}</h1>
        <p className="text-ink-500 text-[14px] mt-2">{t('home.subtitle')}</p>
      </div>

      {/* KPI SECTION — has its own period picker */}
      <SectionHeader
        title={t('home.headline_numbers')}
        sub={t('home.headline_numbers_sub')}
        period={kpiPeriod}
        setPeriod={setKpiPeriod}
      />
      <div className="grid grid-cols-4 gap-5 mb-10">
        {kpi.data ? (
          <>
            <KpiCard icon="dollar-sign" tone="mint"  label={t('income.revenue')}       value={fmtMoney(kpi.data.kpis.revenue.minor, currency, { compact: true })} delta={kpi.data.kpis.revenue.deltaPct} sub={`${FMT_INT.format(kpi.data.kpis.shipments)} ${t('home.shipments')}`} />
            <KpiCard icon="trending-up" tone="royal" label={t('home.profit')}        value={fmtMoney(kpi.data.kpis.profit.minor,  currency, { compact: true })} delta={kpi.data.kpis.profit.deltaPct}  sub={`${t('home.margin')} ${kpi.data.kpis.margin_pct.toFixed(1)}%`} />
            <KpiCard icon="scale"       tone="honey" label={t('home.open_disputes')} value={String(kpi.data.kpis.disputes_open.count)} delta={0} sub={`${fmtMoney(kpi.data.kpis.disputes_open.at_stake_minor, currency, { compact: true })} ${t('home.at_stake')}`} />
            <KpiCard icon="undo-2"      tone="sky2"  label={t('home.open_rebills')}  value={String(kpi.data.kpis.rebills_open.count)}  delta={0} sub={`${fmtMoney(kpi.data.kpis.rebills_open.to_invoice_minor, currency, { compact: true })} ${t('home.to_recover')}`} />
          </>
        ) : (
          Array.from({ length: 4 }).map((_, i) => <div key={i} className="card p-5 h-32 animate-pulse" />)
        )}
      </div>

      {/* PIVOTABLE P&L TREND CHART */}
      <PnlCard
        period={trendPeriod}
        setPeriod={setTrendPeriod}
        currency={currency}
        sym={sym}
        fxScale={fxScale}
      />

      {/* MARGIN BY CARRIER + ALERTS */}
      <div className="grid grid-cols-12 gap-5 mb-10">
        <Card className="col-span-5 p-6">
          <div className="text-[16px] font-semibold tracking-tight mb-1">Needs attention</div>
          <div className="text-[12.5px] text-ink-500 mb-5">Live — based on current open counts</div>
          <div className="space-y-3">
            {kpi.data && kpi.data.alerts.length === 0 && (
              <div className="rounded-xl bg-mint-50 border border-mint-100 p-4 flex items-start gap-3">
                <Icon name="check-circle-2" className="w-5 h-5 text-mint-600 mt-0.5" />
                <div>
                  <div className="text-[14px] font-semibold">All clear</div>
                  <div className="text-[12.5px] text-ink-600 mt-0.5">No open rebills or disputes need attention.</div>
                </div>
              </div>
            )}
            {kpi.data?.alerts.map((a, i) => (
              <div key={i} className={`flex items-start gap-3 p-4 rounded-xl border border-ink-100 bg-${a.tone}-50`}>
                <div className={`w-8 h-8 shrink-0 rounded-lg grid place-items-center bg-${a.tone}-100`}>
                  <Icon name={a.icon as IconName} className={`w-4 h-4 text-${a.tone}-600`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] font-semibold leading-tight">{a.title}</div>
                  <div className="text-[12.5px] text-ink-600 mt-1 leading-relaxed">{a.detail}</div>
                </div>
                <button
                  onClick={() => navigate(a.cta === 'Review' ? '/adjustments' : '/recovery?tab=disputes')}
                  className="shrink-0 h-8 px-3 rounded-lg bg-white text-[12px] font-semibold border border-ink-200 hover:bg-ink-50">
                  {a.cta}
                </button>
              </div>
            ))}
          </div>
        </Card>

        <Card className="col-span-7 p-6">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[16px] font-semibold tracking-tight">Margin by carrier</div>
            <div className="flex items-center gap-2">
              <ChartPeriodPicker value={marginPeriod} onChange={setMarginPeriod} />
              <button onClick={() => navigate('/carriers')} className="text-[12.5px] font-semibold text-royal-600 hover:underline whitespace-nowrap">Deep dive →</button>
            </div>
          </div>
          <div className="text-[12.5px] text-ink-500 mb-5">Profitability per carrier</div>
          {margin.isLoading ? (
            <div className="h-[260px] grid place-items-center text-[13px] text-ink-500">{t('common.loading')}</div>
          ) : !margin.data || margin.data.carriers.filter(c => c.shipments > 0).length === 0 ? (
            <div className="h-[260px] grid place-items-center text-[13px] text-ink-500">No carrier activity in this period.</div>
          ) : (
            <div className="space-y-4">
              {margin.data.carriers.filter(c => c.shipments > 0).slice(0, 6).map(c => {
                const wPct = Math.max(0, Math.min(100, c.margin_pct * 4))
                return (
                  <button
                    key={c.code}
                    onClick={() => navigate(`/carriers/${c.code}`)}
                    className="w-full flex items-center gap-3 text-left hover:bg-ink-50 rounded-lg p-2 -mx-2"
                  >
                    <div className="w-20 text-[13.5px] font-medium flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ background: c.color }} />{c.name}
                    </div>
                    <div className="flex-1 h-2.5 rounded-full bg-ink-100 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${wPct}%`, background: c.color }} />
                    </div>
                    <div className="w-14 text-right num font-semibold text-[13.5px]">{c.margin_pct.toFixed(1)}%</div>
                    <div className="w-20 text-right num text-[13px] text-ink-500">{fmtMoney(c.profit_minor, currency, { compact: true })}</div>
                  </button>
                )
              })}
            </div>
          )}
        </Card>
      </div>

      {/* TOP CUSTOMERS */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-1">
          <div className="text-[16px] font-semibold tracking-tight">Top customers</div>
          <ChartPeriodPicker value={customerPeriod} onChange={setCustomerPeriod} />
        </div>
        <div className="text-[12.5px] text-ink-500 mb-5">By revenue</div>
        {customer.isLoading ? (
          <div className="h-[120px] grid place-items-center text-[13px] text-ink-500">{t('common.loading')}</div>
        ) : !customer.data || customer.data.topCustomers.length === 0 ? (
          <div className="h-[120px] grid place-items-center text-[13px] text-ink-500">No customer activity.</div>
        ) : (
          <div className="grid grid-cols-5 gap-3">
            {customer.data.topCustomers.map((c, i) => (
              <Card key={c.name} className="p-4">
                <div className="text-[11px] font-semibold text-ink-400 uppercase tracking-wider mb-1">#{i + 1}</div>
                <div className="text-[14px] font-semibold leading-tight truncate" title={c.name}>{c.name}</div>
                <div className="num font-bold text-[18px] mt-2">{fmtMoney(c.revenue_minor, currency, { compact: true })}</div>
                <div className="text-[11.5px] text-ink-500 mt-0.5">{FMT_INT.format(c.ships)} shipments</div>
              </Card>
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}

function SectionHeader({ title, sub, period, setPeriod }: {
  title: string; sub: string; period: PeriodKey; setPeriod: (p: PeriodKey) => void
}) {
  return (
    <div className="flex items-end justify-between mb-5">
      <div>
        <div className="text-[16px] font-semibold tracking-tight">{title}</div>
        <div className="text-[12.5px] text-ink-500 mt-1">{sub}</div>
      </div>
      <ChartPeriodPicker value={period} onChange={setPeriod} />
    </div>
  )
}

/* =====================================================================
   PnlCard — pivotable P&L (Total / Carrier / Customer / Service / Country)
   ===================================================================== */

type GroupBy = 'total' | 'carrier' | 'customer' | 'service' | 'state' | 'country'
type Metric  = 'revenue' | 'cost' | 'profit'

interface PnlPayload {
  groupBy: GroupBy
  metric: Metric
  period: { from: string; to: string; days: number; grain: 'day' | 'month' }
  buckets: string[]
  series: Array<{ name: string; dimKey?: string; color: string; values_minor: number[]; fill?: boolean; dashed?: boolean }>
  stacked: boolean
}

const GROUP_OPTS: Array<{ key: GroupBy; label: string }> = [
  { key: 'total',    label: 'Total' },
  { key: 'carrier',  label: 'Carrier' },
  { key: 'customer', label: 'Customer' },
  { key: 'service',  label: 'Service' },
  { key: 'state',    label: 'US state' },
  { key: 'country',  label: 'Country' },
]

const METRIC_OPTS: Array<{ key: Metric; label: string }> = [
  { key: 'revenue', label: 'Revenue' },
  { key: 'cost',    label: 'Cost' },
  { key: 'profit',  label: 'Profit' },
]

const CHART_TYPE_OPTS: Array<{ key: ChartType; label: string }> = [
  { key: 'line',          label: 'Line' },
  { key: 'bar',           label: 'Bar' },
  { key: 'stacked-area',  label: 'Stacked area' },
  { key: 'stacked-bar',   label: 'Stacked bar' },
]

function PnlCard(props: {
  period: PeriodKey
  setPeriod: (p: PeriodKey) => void
  currency: ReturnType<typeof useCurrency>['currency']
  sym: string
  fxScale: number
}) {
  const { t } = useTranslation()
  const [groupBy,   setGroupBy]   = useState<GroupBy>('total')
  const [metric,    setMetric]    = useState<Metric>('revenue')
  const [chartType, setChartType] = useState<ChartType>('line')
  const range = computePeriod(props.period)

  // When switching to a grouped mode, default the chart to stacked-area
  // (more useful for composition). Switching back to Total defaults to line.
  function changeGroupBy(g: GroupBy) {
    setGroupBy(g)
    if (g === 'total' && (chartType === 'stacked-area' || chartType === 'stacked-bar')) setChartType('line')
    if (g !== 'total' && (chartType === 'line' || chartType === 'bar')) setChartType('stacked-area')
  }

  const { data, isLoading } = useQuery<PnlPayload>({
    queryKey: ['pnl', range.from, range.to, groupBy, metric],
    queryFn: () => fetch(
      `/api/home/pnl?from=${range.from}&to=${range.to}&groupBy=${groupBy}&metric=${metric}&limit=8`
    ).then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json() }),
    staleTime: 30_000,
  })

  return (
    <Card className="p-6 mb-10">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="text-[16px] font-semibold tracking-tight">Profit &amp; loss trend</div>
          <div className="text-[12.5px] text-ink-500 mt-1">
            {data ? `${data.period.grain === 'month' ? 'Monthly' : 'Daily'} · ${data.period.from} → ${data.period.to}` : 'Loading…'}
            {groupBy !== 'total' && data && ` · top ${data.series.filter(s => s.name !== 'Other').length} by ${metric}`}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Chart type */}
          <div className="flex items-center gap-1 bg-ink-100 rounded-lg p-0.5">
            {CHART_TYPE_OPTS.map(o => (
              <button
                key={o.key}
                onClick={() => setChartType(o.key)}
                className={`px-2.5 py-1.5 rounded-md text-[12px] font-semibold ${chartType === o.key ? 'bg-white shadow-sm' : 'text-ink-500 hover:text-ink-700'}`}
              >
                {o.label}
              </button>
            ))}
          </div>
          <ChartPeriodPicker value={props.period} onChange={props.setPeriod} />
        </div>
      </div>

      {/* Group by + metric — on a second line so the header doesn't get crowded */}
      <div className="flex flex-wrap items-center gap-3 mb-5 text-[12px]">
        <span className="text-ink-500 font-semibold uppercase tracking-wider">Group by</span>
        <div className="flex items-center gap-1 bg-ink-100 rounded-lg p-0.5">
          {GROUP_OPTS.map(o => (
            <button
              key={o.key}
              onClick={() => changeGroupBy(o.key)}
              className={`px-2.5 py-1.5 rounded-md text-[12px] font-semibold ${groupBy === o.key ? 'bg-white shadow-sm' : 'text-ink-500 hover:text-ink-700'}`}
            >
              {o.label}
            </button>
          ))}
        </div>
        {groupBy !== 'total' && (
          <>
            <span className="text-ink-500 font-semibold uppercase tracking-wider ml-2">Metric</span>
            <div className="flex items-center gap-1 bg-ink-100 rounded-lg p-0.5">
              {METRIC_OPTS.map(o => (
                <button
                  key={o.key}
                  onClick={() => setMetric(o.key)}
                  className={`px-2.5 py-1.5 rounded-md text-[12px] font-semibold ${metric === o.key ? 'bg-white shadow-sm' : 'text-ink-500 hover:text-ink-700'}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {isLoading || !data ? (
        <div className="h-[320px] grid place-items-center text-[13px] text-ink-500">{t('common.loading')}</div>
      ) : data.buckets.length === 0 ? (
        <div className="h-[320px] grid place-items-center text-[13px] text-ink-500">No shipments in this period.</div>
      ) : (
        <TrendChart
          labels={data.buckets}
          series={data.series.map(s => ({
            label: s.name,
            data: s.values_minor.map(v => v / 100 * props.fxScale),
            color: s.color,
            fill: s.fill ?? false,
            dashed: s.dashed,
          }))}
          type={chartType}
          height={320}
          showLegend
          yTickFormat={(v) => props.sym + (Math.abs(v) >= 1000 ? (v / 1000).toFixed(0) + 'K' : v.toFixed(0))}
        />
      )}
    </Card>
  )
}
